import { postClientError } from './telemetry';

const EXTENSION_ASYNC_CHANNEL_CLOSED =
  'A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received';

const MAX_EVENTS_PER_WINDOW = 40;
const WINDOW_MS = 60_000;
const DEDUPE_WINDOW_MS = 15_000;
const MESSAGE_MAX = 1600;

let started = false;
let originalConsoleError: ((...args: any[]) => void) | null = null;
let windowStartMs = Date.now();
let windowCount = 0;
const recentFingerprints = new Map<string, number>();

const trim = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

const normalizeUnknown = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message || value.name;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const allowEvent = (fingerprint: string): boolean => {
  const now = Date.now();
  if (now - windowStartMs >= WINDOW_MS) {
    windowStartMs = now;
    windowCount = 0;
  }
  if (windowCount >= MAX_EVENTS_PER_WINDOW) return false;

  const lastSeen = recentFingerprints.get(fingerprint);
  if (typeof lastSeen === 'number' && now - lastSeen < DEDUPE_WINDOW_MS) return false;

  recentFingerprints.set(fingerprint, now);
  for (const [key, ts] of recentFingerprints.entries()) {
    if (now - ts > DEDUPE_WINDOW_MS) recentFingerprints.delete(key);
  }

  windowCount += 1;
  return true;
};

const emitClientError = (payload: {
  kind: string;
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  column?: number;
  metadata?: Record<string, any>;
}): void => {
  const message = trim(String(payload.message || ''), MESSAGE_MAX);
  if (!message) return;
  if (message.includes(EXTENSION_ASYNC_CHANNEL_CLOSED)) return;
  const fingerprint = [
    payload.kind,
    message,
    payload.source || '',
    String(payload.line ?? ''),
    String(payload.column ?? ''),
  ].join('|');
  if (!allowEvent(fingerprint)) return;

  try {
    void postClientError({
      kind: payload.kind,
      message,
      stack: payload.stack ? trim(payload.stack, 8192) : undefined,
      source: payload.source ? trim(payload.source, 512) : undefined,
      line: payload.line,
      column: payload.column,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      metadata: payload.metadata,
    });
  } catch {
    // Fallback: navigator.sendBeacon for best-effort, non-blocking delivery on page unload
    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const body = JSON.stringify({ kind: payload.kind, message, path: window.location.pathname });
        navigator.sendBeacon('/api/telemetry/client-errors', body);
      }
    } catch {
      // ignore — best-effort only
    }
  }
};

export const initBrowserErrorTelemetry = (): void => {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('error', (event) => {
    const message = event.message || normalizeUnknown(event.error) || 'window_error';
    emitClientError({
      kind: 'unhandled_error',
      message,
      stack: event.error?.stack,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reasonMessage = normalizeUnknown(event.reason);
    if (reasonMessage.includes(EXTENSION_ASYNC_CHANNEL_CLOSED)) return;
    const reasonError = event.reason instanceof Error ? event.reason : undefined;
    emitClientError({
      kind: 'unhandled_rejection',
      message: reasonMessage || 'unhandled_rejection',
      stack: reasonError?.stack,
      metadata: {
        reason_type: typeof event.reason,
      },
    });
  });

  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    originalConsoleError = console.error.bind(console);
    console.error = (...args: any[]) => {
      originalConsoleError?.(...args);
      const combined = trim(args.map(normalizeUnknown).join(' | '), MESSAGE_MAX);
      if (!combined) return;
      emitClientError({
        kind: 'console_error',
        message: combined,
        metadata: { arg_count: args.length },
      });
    };
  }
};

